import Information16 from '@carbon/icons-react/es/information/16';
import React, { FC, useMemo } from 'react';
import styled from 'styled-components';
import { User } from 'api/models';
import {
  Notification as NotificationT,
  NotificationGroup,
  NotificationCallbacks,
} from 'api/notifications';
import { EventType } from 'api/notifications/meta';
import { BodyShort01 } from 'components/styles';
import { useTranslation } from 'hooks/use-translation';
import { fullName } from 'utils/full-name';
import { renderInfo } from './info';
import { Primary } from './primary';

const InfoIcon = styled(Information16)`
  margin-right: var(--spacing-02);
`;

const Content = styled.div`
  display: flex;
  flex: 1 1 100%;
  flex-direction: column;
  ${BodyShort01};
  width: 100%;
`;

const Secondary = styled.div`
  display: flex;
  flex: 1 1 100%;
  margin-left: var(--layout-02);
  margin-top: var(--spacing-02);
  flex-direction: column;
  word-wrap: break-word;
`;

const TimeOffCover = styled.div`
  display: flex;
  flex: 1 1 100%;
  margin-left: var(--layout-02);
  margin-top: var(--spacing-02);
  word-wrap: break-word;
  padding: 4px 0;
  font-size: 12px;
  align-items: center;
  color: var(--gray-50);
`;

export const Notification: FC<{
  group: NotificationGroup;
  notification: NotificationT;
  open: () => void;
  callbacks?: NotificationCallbacks;
}> = ({ group, notification, open, callbacks }) => {
  const info = renderInfo(notification);

  let isOutOfOffice = false;
  let mentionedUsersInTimeOff;

  if (notification.meta.eventType === EventType.UserMentionedInMessage) {
    isOutOfOffice = Boolean(notification.meta.message?.mentionedUsersMap);

    if (isOutOfOffice) {
      mentionedUsersInTimeOff =
        notification.meta.message?.mentionedUsersMap?.data;
    }
  }

  const mentionedUserNames = useMemo(() => {
    let str = '';

    if (mentionedUsersInTimeOff == null) {
      return str;
    }

    mentionedUsersInTimeOff.forEach((user: User, index: number) => {
      str += `${fullName(user)}`;
      if (index < mentionedUsersInTimeOff.length - 1) {
        str += ', ';
      }
    });

    return str;
  }, [mentionedUsersInTimeOff]);

  const outOfOfficeCoverMessagePlural = useTranslation(
    'settings.out_of_office_notification_message.plural',
    { mentionedUserNames },
  );

  const outOfOfficeCoverMessageSingular = useTranslation(
    'settings.out_of_office_notification_message.singular',
    { mentionedUserNames },
  );

  return (
    <Content>
      <Primary
        group={group}
        notification={notification}
        open={open}
        callbacks={callbacks}
      />
      {info != null && <Secondary>{info}</Secondary>}
      {isOutOfOffice && (
        <TimeOffCover data-cy="notifications.outOfOffice.mention">
          <InfoIcon />
          {mentionedUsersInTimeOff.length > 1
            ? outOfOfficeCoverMessagePlural
            : outOfOfficeCoverMessageSingular}
        </TimeOffCover>
      )}
    </Content>
  );
};
